import DotsIcon from '@/assets/icons/dots.svg?react';
import {
  Card,
  CardContent,
  CardDescription,
  CardDivider,
  CardFooter,
  CardFooterItem,
  CardHeader,
  CardHeaderTitle,
  CardTitle,
} from '@/components/ui/card/card.component';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown/dropdown.component';
import { LifecycleStatus, Product } from '@/gql/graphql';
import { productCardMessages } from './product-card.messages';

import { TDate } from '@/components/i18n/date.i18n.component';
import { T } from '@/components/i18n/message.i18n.component';
import { TTime } from '@/components/i18n/time.i18n.component';
import Button from '@/components/ui/button/button.component';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { useIntl } from 'react-intl';

const variants = cva('bg-dark flex flex-row items-center justify-between', {
  variants: {
    variant: {
      default: 'bg-dark text-white-text',
    },
    size: {
      default: 'px-19 h-16 xs:px-0 xs:h-12 w-full',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

interface ProductCardProps extends React.HTMLAttributes<HTMLDivElement>, Omit<VariantProps<typeof variants>, ''> {
  product: Product;
  onCardClick: (product: Product) => void;
  handleEdit: (product: Product) => void;
  handleDelete: (product: Product) => void;
  handleStatusChange: (product: Product) => void;
}

const ProductCard = ({ product, handleEdit, onCardClick, handleStatusChange, handleDelete }: ProductCardProps) => {
  const t = useIntl();

  const handleCardClick = () => {
    onCardClick(product);
  };

  return (
    <Card className="flex flex-col" data-testid={`product-card-${product.id}`} key={product.id}>
      <CardHeader
        className={cn(
          product.lifecycleStatus === LifecycleStatus.Active && 'bg-product-status-active',
          product.lifecycleStatus === LifecycleStatus.Retired && 'bg-product-status-retired',
        )}
      >
        <CardHeaderTitle
          onClick={handleCardClick}
          className={cn('text-product-status-text hover:underline cursor-pointer')}
        >
          {product.lifecycleStatus}
        </CardHeaderTitle>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger data-testid="product-card-dropdown-trigger" className="focus:outline-primary">
            <DotsIcon className="cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              disabled={product.lifecycleStatus === LifecycleStatus.Retired}
              data-testid="product-card-dropdown-trigger-edit"
              onClick={() => {
                if (product.lifecycleStatus === LifecycleStatus.Retired) return;
                handleEdit(product);
              }}
            >
              <T {...productCardMessages.editProduct} />
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="product-card-dropdown-trigger-status-change"
              onClick={() => handleStatusChange(product)}
              className={cn(product.lifecycleStatus === LifecycleStatus.Active && 'text-system-error')}
            >
              {product.lifecycleStatus === LifecycleStatus.Active && <T {...productCardMessages.setAsRetired} />}
              {product.lifecycleStatus === LifecycleStatus.Retired && <T {...productCardMessages.setAsActive} />}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="product-card-dropdown-trigger-delete"
              onClick={() => handleDelete(product)}
              className="text-system-error"
            >
              <T {...productCardMessages.deleteProduct} />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <Button variant="ghost" onClick={handleCardClick}>
          <CardTitle
            aria-label="Product `name card title button"
            className="hover:underline cursor-pointer"
            data-testid={`product-card-${product.id}-title`}
          >
            {product.name}
          </CardTitle>
        </Button>
        <CardDescription data-testid={`product-card-${product.id}-description`}>{product.description}</CardDescription>
      </CardContent>
      <CardDivider />
      <CardContent className="flex flex-1">
        <CardFooter className="flex flex-1 flex-col justify-between">
          <CardFooterItem>
            <p className="typo-secondary text-secondary-text">
              <T {...productCardMessages.version} />
            </p>
            <p
              title={product.version}
              data-testid={`product-card-${product.id}-version`}
              className="typo-secondary truncate w-32 text-end"
            >
              {product.version}
            </p>
          </CardFooterItem>

          <div>
            <p data-testid={`product-card-${product.id}-updatedAt`} className="typo-caption text-secondary-text mt-2">
              {t.formatMessage(productCardMessages.updatedOn, {
                date: <TDate value={new Date(product.updatedAt)} />,
                time: <TTime value={new Date(product.updatedAt)} />,
              })}
            </p>
          </div>
        </CardFooter>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
